import { spiralRotationDeg } from '@stair-craft/calc/util';
import { IStairCalculationResult, IStairConfig } from '@stair-craft/common/util';

import { sectorBulges } from './arc-bulge';
import { horizontalDim, verticalDim } from './dim-builders';
import {
    EColorRef,
    EPrimitiveKind,
    ETextAlign,
    ETextBaseline,
    FONT_MONO_FAMILY,
    IFont,
    IPaneRect,
    IPlanPrimitive,
    IPoint,
    IProjectionOutput,
    emptyProjection,
} from './plan-primitive';
import { ITextMeasurer } from './text-measurer';

const POLE_RADIUS: number = 140; // matches spiral.calculator.ts
const PAD_LEFT: number = 56;
const PAD_RIGHT: number = 64;
const PAD_TOP: number = 36;
const PAD_BOTTOM: number = 40;
const FIT_FACTOR: number = 0.85;
const DEFAULT_DIAMETER: number = 1600;

const ANGLE_FONT: IFont = { weight: 400, size: 11, family: FONT_MONO_FAMILY };

// Проекторы объявлены свойствами-функциями, а не методами: их разбирают
// деструктуризацией (`const { wx, wy } = projector`), и отвязанный от объекта
// метод потерял бы `this`.
interface IProjector {
    readonly wx: (mm: number) => number;
    readonly wy: (mm: number) => number;
}

/**
 * Spiral step detail (чертёж ступеней, винтовая) — one winder tread as a wedge
 * (apex at the pole, opening to the outer radius), with the apex-angle arc/label
 * and the inner/outer chord and radial-depth dimensions. Pure projection in
 * pane-local pixels. 1:1 port of PlanViewComponent#drawSpiralStepDetail.
 */
export function projectSpiralStepDetail(
    result: IStairCalculationResult,
    config: IStairConfig,
    pane: IPaneRect,
    measurer: ITextMeasurer
): IProjectionOutput {
    const diameter: number = config.spiralDiameter ?? DEFAULT_DIAMETER;
    const outerR: number = diameter / 2;
    const innerR: number = POLE_RADIUS;
    const totalRotationDeg: number = spiralRotationDeg(config);
    const N: number = result.stepCount;
    if (N < 2 || outerR <= innerR) {
        return emptyProjection();
    }

    const anglePerStepRad: number = (totalRotationDeg * Math.PI) / 180 / (N - 1);
    const anglePerStepDeg: number = totalRotationDeg / (N - 1);
    const innerChord: number = Math.round(2 * innerR * Math.sin(anglePerStepRad / 2));
    const outerChord: number = Math.round(2 * outerR * Math.sin(anglePerStepRad / 2));
    const treadW: number = Math.round(outerR - innerR);

    const apexToOuterX: number = outerR;
    const halfH: number = outerR * Math.sin(anglePerStepRad / 2);
    const innerW: number = pane.w - PAD_LEFT - PAD_RIGHT;
    const innerHeightPx: number = pane.h - PAD_TOP - PAD_BOTTOM;
    if (innerW <= 0 || innerHeightPx <= 0) {
        return emptyProjection();
    }

    const scale: number = Math.min(innerW / apexToOuterX, innerHeightPx / Math.max(1, halfH * 2)) * FIT_FACTOR;
    const drawW: number = apexToOuterX * scale;
    const drawH: number = halfH * 2 * scale;
    const ox: number = PAD_LEFT + (innerW - drawW) / 2;
    const oy: number = pane.h - PAD_BOTTOM - drawH / 2;

    const projector: IProjector = {
        wx: (mm: number): number => ox + mm * scale,
        wy: (mm: number): number => oy - mm * scale,
    };
    const { wx, wy }: IProjector = projector;

    const halfAngleRad: number = anglePerStepRad / 2;
    const innerTopY: number = innerR * Math.sin(halfAngleRad);
    const innerBotY: number = -innerTopY;
    const outerTopY: number = outerR * Math.sin(halfAngleRad);
    const outerBotY: number = -outerTopY;
    const innerEdgeX: number = innerR * Math.cos(halfAngleRad);
    const outerEdgeX: number = outerR * Math.cos(halfAngleRad);

    const primitives: IPlanPrimitive[] = [];

    // Wedge outline — apex toward the pole, opening to the outer radius; the outer and inner
    // edges are arcs around the pole.
    const wedge: IPoint[] = [
        [wx(innerEdgeX), wy(innerTopY)],
        [wx(outerEdgeX), wy(outerTopY)],
        [wx(outerEdgeX), wy(outerBotY)],
        [wx(innerEdgeX), wy(innerBotY)],
    ];
    primitives.push({
        kind: EPrimitiveKind.Poly,
        points: wedge,
        bulges: sectorBulges(wedge[0], wedge[1], wedge[2], wedge[3]),
        closed: true,
        fill: EColorRef.Winder,
        stroke: EColorRef.StepStroke,
        width: 1.4,
    });

    // Apex angle indicator arc + label.
    const arcR: number = Math.min(innerEdgeX * 0.55, 80);
    primitives.push({
        kind: EPrimitiveKind.Arc,
        center: [wx(0), wy(0)],
        radius: arcR * scale,
        startAngle: -halfAngleRad,
        endAngle: halfAngleRad,
        stroke: EColorRef.Ink3,
        width: 0.8,
    });
    primitives.push({
        kind: EPrimitiveKind.Text,
        // Left of the apex, where the pole hole leaves the paper clear, not on the narrow tip.
        at: [wx(0) - 6, wy(0)],
        text: `${anglePerStepDeg.toFixed(1)}°`,
        align: ETextAlign.Right,
        baseline: ETextBaseline.Middle,
        color: EColorRef.Ink2,
        font: ANGLE_FONT,
    });

    // Dimensions.
    primitives.push(horizontalDim(wx(outerEdgeX), wy(outerTopY) - 18, wx(outerEdgeX) + drawW * 0.15, String(outerChord), measurer));
    if (innerEdgeX > 0 && innerTopY > 4) {
        primitives.push(verticalDim(wx(innerEdgeX) - 14, wy(innerTopY), wy(innerBotY), String(innerChord), measurer));
    }
    primitives.push(verticalDim(wx(outerEdgeX) + 18, wy(outerTopY), wy(outerBotY), String(outerChord), measurer));
    primitives.push(horizontalDim(wx(innerEdgeX), wy(outerBotY) + 24, wx(outerEdgeX), String(treadW), measurer));

    // Millimetre origin: the wedge apex at the pole axis (radius 0, offset 0).
    return { primitives, pxPerMm: scale, origin: { x: ox, y: oy } };
}
